

































































import Vue from '@front-app/vendor/vue';
import {
	computed,
	defineComponent,
	nextTick,
	PropType,
	Ref,
	ref,
	watch,
} from '@vue/composition-api';
import {
	eventHasAdminRole,
	eventHasInviteeRole,
	eventHasMemberRole,
} from '@front-app/util/permission';
import { EventBucket, EventBucketType, useEventBuckets } from '@front-app/hooks/useEventBuckets';
import { useGoToEventDates } from '@front-app/hooks/useGoToEventDates';
import { DropdownPosition } from '@front-app/components/dropdowns/dropdownPosition';
import { unique } from '@front-app/util/array';
import LoaderState from '@front-app/components/LoaderState.vue';
import { EventV2 } from '@front-app/services/EventsApi/EventModel';
import { sum } from '@front-app/util/math';
import { useFilter } from '@front-app/hooks/useFilter';
import PaddedContent from '@front-app/components/layouts/columns/PaddedContent.vue';
import DayList from '@front-app/views/home/events/DayList.vue';
import GridList from '@front-app/views/home/events/GridList.vue';
import { EventFilter, FILTER_BUTTON_ORDER } from '@front-app/views/home/events/EventEnum';
import EventFilters from '@front-app/views/home/events/EventFilters.vue';
import { BannerLocation } from '@front-app/services/ActionApi/UserActionRequestModel';

const TODAY = 'today';

export default defineComponent({
	components: {
		LoaderState,
		PaddedContent,
		DayList,
		GridList,
		EventFilters,
	},
	props: {
		groupId: {
			type: String,
			default: '',
		},
		events: {
			type: Array as PropType<EventV2[]>,
			required: true,
		},
		currentAccessFilters: {
			type: Array as PropType<EventFilter[]>,
			required: true,
		},
		currentTypeFilters: {
			type: Array as PropType<string[]>,
			required: true,
		},
		currentTopicFilters: {
			type: Array as PropType<string[]>,
			required: true,
		},
		headerOffsetSelectors: {
			type: Array as PropType<string[]>,
			required: true,
		},
		bannerLocation: {
			type: String as PropType<BannerLocation>,
			required: true,
		},
		requestScroll: Boolean,
		areEventsLoading: Boolean,
	},
	setup(props) {
		const eventsRef = computed(() => props.events);
		// Types in URLs
		const availableTypeFilters = computed(() => unique(props.events.flatMap((e) => e.eventTypes)));
		// End Types in URLs

		// Filters in URLs
		const availableTopicFilters = computed(() => unique(props.events.flatMap((e) => e.categories)));
		// End Filters in URLs

		// Search
		const searchValue = ref('');
		const baseFilteredEvents = computed(() => props.events
			.filter((e: EventV2) => e.name.toLocaleLowerCase().includes(searchValue.value.toLocaleLowerCase())));
		// End Search

		// Access filters
		const filteredEventsByAccess: Record<EventFilter, Ref<EventV2[]>> = {
			[EventFilter.ADMINISTERED]: useFilter(baseFilteredEvents, eventHasAdminRole),
			[EventFilter.INVITES]: useFilter(baseFilteredEvents, eventHasInviteeRole),
			[EventFilter.REGISTERED]: useFilter(baseFilteredEvents, eventHasMemberRole),
		};
		const totalNumAdminEvents = useFilter(eventsRef, eventHasAdminRole);
		const accessFilterCounts = computed(() => ({
			[EventFilter.ADMINISTERED]: filteredEventsByAccess[EventFilter.ADMINISTERED].value.length,
			[EventFilter.INVITES]: filteredEventsByAccess[EventFilter.INVITES].value.length,
			[EventFilter.REGISTERED]: filteredEventsByAccess[EventFilter.REGISTERED].value.length,
		}));
		const availableAccessFilters = computed(() => FILTER_BUTTON_ORDER
			// It's possible to have an empty filter selected because of direct navigation, or toggling the "past filter"
			// in this case, it's easiest just to keep the button visible for clarity
			.filter((key) => {
				if (key !== EventFilter.ADMINISTERED) {
					return true;
				}
				return totalNumAdminEvents.value.length > 0;
			}));
		// End: Access filters

		const eventsForSelectedFilters = computed(() => props.currentAccessFilters
			.map((filter) => filteredEventsByAccess[filter].value)
			.reduce((acc, curr) => acc.concat(curr), [])
			.filter((e) => props.currentTopicFilters.length === 0
				|| props.currentTopicFilters.some((cat) => e.categories.includes(cat)))
			.filter((e) => props.currentTypeFilters.length === 0
				|| props.currentTypeFilters.some((type) => e.eventTypes.includes(type))));

		const {
			bucketedEvents: visibleEvents,
			hasActiveEvents,
			hasComingSoonEvents,
		} = useEventBuckets(eventsForSelectedFilters);

		// Event Counts
		const numEvents = computed(() => props.events.length);
		const numVisibleEvents = computed(() => eventsForSelectedFilters.value.length);
		// END Counts

		// Go to date
		const goToEventDates = useGoToEventDates(eventsRef);
		const selectedGoToDate = ref(goToEventDates.todayKey);
		const eventsEl = ref(null as null | Vue);
		const getTargetEl = (key: string, parent: HTMLInputElement, targetDate: Date): HTMLInputElement | undefined => {
			let targetElement;

			if (key === TODAY) {
				if (hasActiveEvents.value) {
					targetElement = parent.querySelector(`[data-today-bucket="${EventBucketType.ACTIVE}"]`) as HTMLInputElement;
				} else if (hasComingSoonEvents.value) {
					targetElement = parent.querySelector(`[data-today-bucket="${EventBucketType.COMING_SOON}"]`) as HTMLInputElement;
				}
			}

			if (targetElement) {
				return targetElement;
			}

			return parent.querySelector(`[data-month="${targetDate.getMonth()}"][data-year="${targetDate.getFullYear()}"]`) as undefined | HTMLInputElement;
		};
		const getTargetDate = (key: string): Date => {
			if (key === TODAY) {
				if (hasActiveEvents.value) {
					const activeBucket = visibleEvents.value.find((bucket) => bucket.type === EventBucketType.ACTIVE);
					if (activeBucket) {
						return new Date(activeBucket.date);
					}
				} else if (hasComingSoonEvents.value) {
					const comingSoonBucket = visibleEvents.value.find((bucket) => bucket.type === EventBucketType.COMING_SOON);
					if (comingSoonBucket) {
						return new Date(comingSoonBucket.date);
					}
				} else {
					return goToEventDates.getClosestNonTodayDate();
				}
			}
			return goToEventDates.getDateByKey(key);
		};
		const goToDate = async (key: string): Promise<void> => {
			selectedGoToDate.value = key === TODAY ? goToEventDates.todayKey : key;
			const targetDate = getTargetDate(key);
			await nextTick();

			if (!eventsEl.value) {
				if (key !== TODAY) {
					goToDate(TODAY);
					return;
				}
				// eslint-disable-next-line no-console
				console.warn('No events element');
				return;
			}

			const targetEl = getTargetEl(key, eventsEl.value.$el as HTMLInputElement, targetDate);
			if (!targetEl) {
				if (key !== TODAY) {
					goToDate(TODAY);
					return;
				}
				// eslint-disable-next-line no-console
				console.error('No element with correct date');
				return;
			}
			targetEl.focus();
			const headerOffset = ['.event__filters', ...props.headerOffsetSelectors]
				.flatMap((sel) => Array.from(document.querySelectorAll(sel)) as Array<HTMLElement | undefined>)
				.filter(Boolean)
				.map((item) => item?.offsetHeight || 0)
				.reduce(sum, 0);
			const elementPosition = targetEl.getBoundingClientRect().top;
			const offsetPosition = elementPosition - headerOffset;
			window.scrollBy({
				top: offsetPosition,
			});
		};
		watch(() => props.areEventsLoading, (curr, prev) => {
			if (!curr && curr !== prev) {
				goToDate(TODAY);
			}
		});
		watch(() => props.requestScroll, (curr, prev) => {
			if (curr !== prev) {
				goToDate(TODAY);
			}
		});
		// End Go to date

		return {
			// Counts
			numEvents,
			numVisibleEvents,
			// Search
			searchValue,
			// Go to date
			eventsEl,
			selectedGoToDate,
			goToDate,
			monthsAndYears: goToEventDates.options,
			getDateByKey: goToEventDates.getDateByKey,
			// Filters
			visibleEvents,
			availableTypeFilters,
			availableTopicFilters,
			accessFilterCounts,
			availableAccessFilters,
			// Util
			BOTTOM_RIGHT: DropdownPosition.BOTTOM_RIGHT,
			isInvitesList: computed(() => props.currentAccessFilters.length === 1 && props.currentAccessFilters.includes(EventFilter.INVITES)),
			toFlatEventList(buckets: EventBucket[]): EventV2[] {
				return buckets.flatMap((_) => _.events);
			},
		};
	},
});
